import DashLayout from "@/layouts/DashLayout";
import { useGetMenuByIdQuery } from "@/redux/services/menu/menu.api";
import { Menu, MenuItem, Section } from "@/redux/services/menu/menu.type";
import { Plus, Pencil, UtensilsCrossed } from "lucide-react";
import BackLink from "@/components/BackLink";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MenuItemCard, { MenuItemCardSkeleton } from "./MenuItemCard";
import SectionItemCard, { SectionItemCardSkeleton } from "./SectionItemCard";
import { useModal } from "@/lib/hooks";
import MenuItemDetailsDrawer from "@/components/menu/MenuItemDetailsDrawer";
import MenuFormModal from "@/components/menu/MenuFormModal";
import NoMatch from "@/pages/Errors/NoMatch";
import SectionFormModal from "@/components/menu/SectionFormModal";
import MenuItemFormDrawer from "@/components/menu/MenuItemFormDrawer";
import { ButtonAdd } from "@/components/ui/button";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { usePermissions } from "@/hooks/usePermissions";
import { SearchInput } from "@/components/SearchInput";
import MenuComboList from "@/components/menu/combos/MenuComboList";
import AddExistingDishModal from "@/components/menu/AddExistingDishModal";

const MenuDetailsPage = () => {
  const { currentUser } = useAppSelector((s) => s.user);
  const { canManage } = usePermissions();
  const isReadOnly =
    currentUser?.roles?.includes(Roles.owner) || !canManage("menu");
  const { id } = useParams();
  const [selectedSection, setSelectedSection] = useState<Section>();
  const [isAddExistingDishOpen, setIsAddExistingDishOpen] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(0);
  const { data: menu, isLoading, refetch } = useGetMenuByIdQuery(id as string);

  const {
    isOpen: isEditMenuOpen,
    openEditModal: openEditMenu,
    closeModal: closeEditMenu,
  } = useModal<Menu>();

  const {
    item: selectedSectionForEdit,
    isOpen: isSectionFormOpen,
    openModal: openAddSectionForm,
    closeModal: closeSectionForm,
  } = useModal<Section>();

  // Menu item details drawer
  const [detailsMenuItem, setDetailsMenuItem] = useState<MenuItem | null>(null);

  // Menu item form drawer
  const {
    item: selectedMenuItemForEdit,
    isOpen: isMenuItemFormOpen,
    openEditModal: openMenuItemEditModal,
    openModal: openAddMenuItem,
    closeModal: closeMenuItemForm,
  } = useModal<MenuItem>();
  const [searchQuery, setSearchQuery] = useState("");

  // Add this function to filter menu items
  const filteredMenuItems = selectedSection?.menuItems?.filter((menuItem) =>
    menuItem.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEditFromDetails = (item: MenuItem) => {
    setDetailsMenuItem(null);
    openMenuItemEditModal(item);
  };

  useEffect(() => {
    if (menu?.menuSections && menu.menuSections.length > 0) {
      if (selectedSectionIndex < menu.menuSections.length) {
        setSelectedSection(menu.menuSections[selectedSectionIndex]);
      } else {
        setSelectedSection(menu.menuSections[0]);
        setSelectedSectionIndex(0);
      }
    }
  }, [menu, selectedSectionIndex]);

  const handleSectionClick = (section: Section, index: number) => {
    setSelectedSection(section);
    setSelectedSectionIndex(index);
  };

  const onSectionFormSuccess = () => {
    closeSectionForm();
    refetch();
  };

  const onMenuItemFormSuccess = () => {
    closeMenuItemForm();
    refetch();
  };

  if (isLoading) {
    return (
      <DashLayout>
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="space-y-2 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-32" />
            <div className="h-8 bg-gray-200 rounded w-48" />
          </div>

          {/* Section Tabs Skeleton */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {[...Array(4)].map((_, i) => (
              <SectionItemCardSkeleton key={i} />
            ))}
          </div>

          {/* Search Bar Skeleton */}
          <div className="h-10 bg-gray-200 rounded w-64 animate-pulse" />

          {/* Menu Items Grid Skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(8)].map((_, i) => (
              <MenuItemCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </DashLayout>
    );
  }

  if (!menu) {
    return <NoMatch />;
  }

  return (
    <DashLayout>
      <div className="space-y-6">
        <div className="space-y-2">
          <BackLink to="/menus" label="Retour aux menus" />
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold">
                {menu.title}
              </h2>
              {!isReadOnly && (
                <button
                  onClick={() => openEditMenu(menu)}
                  className="text-muted-foreground hover:text-primary"
                  title="Modifier le menu"
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Menu Combos Section */}
        <MenuComboList menuId={id as string} isReadOnly={isReadOnly} />

        {menu?.menuSections && menu.menuSections.length > 0 ? (
          <>
            {/* Section tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scroll-hidden">
              {!isReadOnly && (
                <button
                  onClick={openAddSectionForm}
                  className="flex items-center gap-1.5 py-2 px-3 rounded-full border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-all text-sm whitespace-nowrap"
                  title="Ajouter une section"
                >
                  <Plus size={14} />
                  Section
                </button>
              )}

              {menu?.menuSections?.map((section, index) => (
                <SectionItemCard
                  key={section.id}
                  menuId={id as string}
                  section={section}
                  selectedSection={selectedSection}
                  handleSectionClick={() => handleSectionClick(section, index)}
                  isReadOnly={isReadOnly}
                />
              ))}
            </div>

            {/* Search */}
            <div className="max-w-sm">
              <SearchInput
                placeholder="Rechercher un élément..."
                value={searchQuery}
                onChange={(value) => setSearchQuery(value.trim())}
              />
            </div>

            {/* Menu items grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {!isReadOnly && (
                <button
                  className="border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center min-h-[200px] cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                  onClick={() => openAddMenuItem()}
                >
                  <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center mb-2">
                    <Plus size={20} className="text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Nouveau plat
                  </p>
                </button>
              )}

              {!isReadOnly && (
                <button
                  className="border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center min-h-[200px] cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                  onClick={() => setIsAddExistingDishOpen(true)}
                  title="Placer sur ce menu un plat que vous avez déjà"
                >
                  <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center mb-2">
                    <UtensilsCrossed size={20} className="text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Plat existant
                  </p>
                </button>
              )}

              {filteredMenuItems && filteredMenuItems?.length > 0 ? (
                filteredMenuItems?.map((menuItem) => (
                  <MenuItemCard
                    key={menuItem.id}
                    menuItem={menuItem}
                    openMenuItemModal={setDetailsMenuItem}
                    isReadOnly={isReadOnly}
                  />
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <p className="text-lg font-medium mb-1">Aucun élément</p>
                  <p className="text-sm">
                    Aucun élément trouvé pour cette section
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground text-lg mb-4">
              Aucune section trouvée pour ce menu
            </p>
            {!isReadOnly && (
              <ButtonAdd
                onClick={openAddSectionForm}
                label="Ajouter une section"
              />
            )}
          </div>
        )}
      </div>

      <MenuItemDetailsDrawer
        item={detailsMenuItem}
        isOpen={!!detailsMenuItem}
        onClose={() => setDetailsMenuItem(null)}
        onEdit={handleEditFromDetails}
        onAfterDelete={refetch}
      />

      <MenuFormModal
        isOpen={isEditMenuOpen}
        onClose={closeEditMenu}
        menu={menu}
      />

      <SectionFormModal
        isOpen={isSectionFormOpen}
        onClose={closeSectionForm}
        section={selectedSectionForEdit}
        menuId={id as string}
        numberOfMenuSections={menu?.menuSections?.length ?? 0}
        onSuccess={onSectionFormSuccess}
      />

      <AddExistingDishModal
        isOpen={isAddExistingDishOpen}
        onClose={() => setIsAddExistingDishOpen(false)}
        section={selectedSection}
        onSuccess={refetch}
      />

      <MenuItemFormDrawer
        isOpen={isMenuItemFormOpen}
        onClose={closeMenuItemForm}
        menuItem={selectedMenuItemForEdit}
        section={selectedSection}
        onSuccess={onMenuItemFormSuccess}
      />
    </DashLayout>
  );
};

export default MenuDetailsPage;
